/**
 * IndexedDB Session Service
 * Persists batch recording chunks locally to survive page refreshes.
 *
 * Storage Strategy:
 * - Saves chunks during recording for recovery
 * - Cleans up on successful upload or discard
 * - Auto-deletes recordings older than 24 hours
 */

import { createLogger } from '@/lib/logger';

const log = createLogger('indexedDBSessionService');

const DB_NAME = 'systemdox-recordings';
const DB_VERSION = 1;
const STORE_NAME = 'interrupted-recordings';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

export interface CapturedPhoto {
  id: string;                    // uuid for uniqueness
  blob: Blob;                    // JPEG blob from canvas
  timestamp: number;             // seconds into recording
  capturedAt: number;            // Date.now() for sorting
  thumbnailDataUrl?: string;     // base64 thumbnail for preview (optional)
}

export interface RecordingNote {
  id: string;                    // uuid for uniqueness
  text: string;                  // note content
  timestamp: number;             // seconds into recording
  createdAt: number;             // Date.now() for sorting
}

export interface InterruptedRecording {
  sessionId: string;
  chunks: Blob[];
  mimeType: string;
  duration: number;
  languageCode: string;
  startedAt: number;
  lastSavedAt: number;
  photos?: CapturedPhoto[];
  notes?: RecordingNote[];
}

class IndexedDBSessionService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  /**
   * Initialize IndexedDB connection
   */
  private async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        const error = request.error || new Error('Failed to open IndexedDB');
        log.error('Failed to open IndexedDB', error as Error);
        reject(new Error('Failed to open IndexedDB'));
      };

      request.onsuccess = () => {
        this.db = request.result;
        log.info('IndexedDB connection opened');
        resolve();
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'sessionId' });
          store.createIndex('startedAt', 'startedAt', { unique: false });
          log.info('Created object store', { storeName: STORE_NAME });
        }
      };
    });

    return this.initPromise;
  }

  /**
   * Save recording to IndexedDB
   */
  async saveRecording(recording: InterruptedRecording): Promise<void> {
    try {
      await this.init();
      if (!this.db) throw new Error('DB not initialized');

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.put(recording);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      log.debug('Recording saved to IndexedDB', {
        sessionId: recording.sessionId,
        chunks: recording.chunks.length,
        duration: recording.duration
      });
    } catch (err) {
      log.error('Failed to save recording', err as Error);
      throw err;
    }
  }

  /**
   * Get a specific recording by session ID
   */
  async getRecording(sessionId: string): Promise<InterruptedRecording | null> {
    try {
      await this.init();
      if (!this.db) throw new Error('DB not initialized');

      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      return new Promise((resolve, reject) => {
        const request = store.get(sessionId);
        request.onsuccess = () => resolve(request.result || null);
        request.onerror = () => reject(request.error);
      });
    } catch (err) {
      log.error('Failed to get recording', err as Error);
      return null;
    }
  }

  /**
   * Get all interrupted recordings (excluding stale ones)
   */
  async getAllRecordings(): Promise<InterruptedRecording[]> {
    try {
      await this.init();
      if (!this.db) throw new Error('DB not initialized');

      const transaction = this.db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);

      const recordings = await new Promise<InterruptedRecording[]>((resolve, reject) => {
        const request = store.getAll();
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = () => reject(request.error);
      });

      // Filter out stale recordings (older than 24 hours)
      const now = Date.now();
      const validRecordings = recordings.filter(r => (now - r.startedAt) < MAX_AGE_MS);

      // Delete stale recordings in background
      const staleRecordings = recordings.filter(r => (now - r.startedAt) >= MAX_AGE_MS);
      if (staleRecordings.length > 0) {
        log.info('Found stale recordings, cleaning up', { count: staleRecordings.length });
        Promise.all(staleRecordings.map(r => this.deleteRecording(r.sessionId)));
      }

      log.debug('Retrieved recordings from IndexedDB', {
        total: recordings.length,
        valid: validRecordings.length,
        stale: staleRecordings.length
      });

      return validRecordings;
    } catch (err) {
      log.error('Failed to get recordings', err as Error);
      return [];
    }
  }

  /**
   * Delete a recording by session ID
   */
  async deleteRecording(sessionId: string): Promise<void> {
    try {
      await this.init();
      if (!this.db) throw new Error('DB not initialized');

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.delete(sessionId);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      log.info('Recording deleted from IndexedDB', { sessionId });
    } catch (err) {
      log.error('Failed to delete recording', err as Error);
      throw err;
    }
  }

  /**
   * Clear all recordings (for cleanup or reset)
   */
  async clearAll(): Promise<void> {
    try {
      await this.init();
      if (!this.db) throw new Error('DB not initialized');

      const transaction = this.db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);

      await new Promise<void>((resolve, reject) => {
        const request = store.clear();
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      log.info('All recordings cleared from IndexedDB');
    } catch (err) {
      log.error('Failed to clear recordings', err as Error);
      throw err;
    }
  }

  /**
   * Check storage quota availability
   */
  async checkStorageQuota(): Promise<{ available: boolean; usage?: number; quota?: number }> {
    try {
      if ('storage' in navigator && 'estimate' in navigator.storage) {
        const estimate = await navigator.storage.estimate();
        const usage = estimate.usage || 0;
        const quota = estimate.quota || 0;
        const available = quota > 0 && (usage / quota) < 0.9; // Less than 90% used

        log.debug('Storage quota check', {
          usage: `${(usage / 1024 / 1024).toFixed(2)}MB`,
          quota: `${(quota / 1024 / 1024).toFixed(2)}MB`,
          available
        });

        return { available, usage, quota };
      }

      // Quota API not available, assume available
      return { available: true };
    } catch (err) {
      log.warn('Failed to check storage quota', { error: err instanceof Error ? err.message : 'Unknown' });
      return { available: true }; // Assume available on error
    }
  }

  /**
   * Close IndexedDB connection
   */
  close(): void {
    if (this.db) {
      this.db.close();
      this.db = null;
      this.initPromise = null;
      log.info('IndexedDB connection closed');
    }
  }
}

// Export singleton instance
export const indexedDBSessionService = new IndexedDBSessionService();
